import { Card, Stack, Text } from "@mantine/core";
import { memo } from "react";
import { MantineBarChart } from "../../../Charts/BarChart/MantineBarChart";
import { MantineLineChart } from "../../../Charts/LineChart/MantineLineChart";
import type {
  ToolResultChart,
  ToolResultChartSeries
} from "../ToolResultRenderer.types";

interface ChartCardProps {
  data: ToolResultChart;
}

const ATTRIBUTED_SHARE_FOOTNOTE =
  "Einspeisemengen sind über den konfigurierten Erzeugungsanteil aus der Messung an der Marktlokation abgeleitet, nicht je WEA gemessen.";

function compareChartX(a: string, b: string): number {
  const numA = Number(a);
  const numB = Number(b);
  if (!Number.isNaN(numA) && !Number.isNaN(numB)) {
    return numA - numB;
  }
  return a.localeCompare(b);
}

/** Merges the series' points into one Mantine chart row per x value, ordered chronologically. */
export function buildMantineData(
  series: Array<ToolResultChartSeries>
): Array<Record<string, string | number>> {
  const pointMap = new Map<string, Record<string, string | number>>();
  for (const s of series) {
    for (const point of s.points) {
      const x = String(point.x);
      const row = pointMap.get(x) ?? { x };
      row[s.name] = point.y;
      pointMap.set(x, row);
    }
  }

  return Array.from(pointMap.entries())
    .sort(([a], [b]) => compareChartX(a, b))
    .map(([, row]) => row);
}

/**
 * Discloses how much of a series was actually measured, as a German footnote — the
 * whole-period figure plus the intervals that under-report. Null when fully covered.
 */
export function buildCoverageFootnote(
  series: ToolResultChartSeries
): string | null {
  const incompletePoints = series.points.filter(
    (point) =>
      point.coverage_percent !== undefined && point.coverage_percent < 100
  );
  const isPeriodIncomplete =
    series.available_percent !== undefined && series.available_percent < 100;

  if (!isPeriodIncomplete && incompletePoints.length === 0) {
    return null;
  }

  const parts: Array<string> = [];

  if (isPeriodIncomplete) {
    parts.push(`Datenverfügbarkeit ${series.available_percent} %`);
  }

  if (incompletePoints.length > 0) {
    const listed = incompletePoints
      .map((point) => `${point.x} (${point.coverage_percent} %)`)
      .join(", ");
    parts.push(`unvollständige Messwerte: ${listed}`);
  }

  return `${series.name}: ${parts.join(" — ")}`;
}

function ChartFootnotes({ data }: ChartCardProps) {
  const showAttributedShareFootnote = data.series.some(
    (s) => s.attributed_share_percent !== undefined
  );
  const coverageFootnotes = data.series
    .map(buildCoverageFootnote)
    .filter((footnote): footnote is string => footnote !== null);

  if (
    !data.unavailable?.length &&
    !showAttributedShareFootnote &&
    coverageFootnotes.length === 0
  ) {
    return null;
  }

  return (
    <Stack gap={4} mt="xs">
      {data.unavailable?.map((u) => (
        <Text c="dimmed" key={u.name} size="xs">
          {u.name}: {u.reason}
        </Text>
      ))}
      {coverageFootnotes.map((footnote) => (
        <Text c="dimmed" key={footnote} size="xs">
          {footnote}
        </Text>
      ))}
      {showAttributedShareFootnote && (
        <Text c="dimmed" fs="italic" size="xs">
          {ATTRIBUTED_SHARE_FOOTNOTE}
        </Text>
      )}
    </Stack>
  );
}

function ChartCardComponent({ data }: ChartCardProps) {
  const ariaLabel = `${data.y_axis_label} nach ${data.x_axis_label}`;

  if (data.series.length === 0) {
    return (
      <Card aria-label={ariaLabel} padding="sm" role="region" withBorder>
        <Text c="dimmed" fs="italic" size="sm">
          Keine Daten verfügbar.
        </Text>
        <ChartFootnotes data={data} />
      </Card>
    );
  }

  const mantineData = buildMantineData(data.series);
  const mantineSeries = data.series.map((s) => ({ name: s.name }));

  const sharedProps = {
    data: mantineData,
    dataKey: "x",
    series: mantineSeries,
    xAxisLabel: data.x_axis_label,
    yAxisLabel: data.y_axis_label,
    withLegend: true,
    h: 300
  };

  return (
    <Card aria-label={ariaLabel} padding="sm" role="region" withBorder>
      {data.chart_type === "bar" && <MantineBarChart {...sharedProps} />}
      {data.chart_type === "line" && <MantineLineChart {...sharedProps} />}
      {data.chart_type !== "bar" && data.chart_type !== "line" && (
        <Text c="dimmed" fs="italic" size="sm">
          Diagrammtyp wird nicht unterstützt.
        </Text>
      )}
      <ChartFootnotes data={data} />
    </Card>
  );
}

const ChartCard = memo(ChartCardComponent);

export { ChartCard };
